// The words the game card and the picked row print under a win %. Pure, so
// what they say is tested here rather than by loading the page.
//
// One line, in this order, and nothing hidden at any width:
//
//   +5.1% value · crosses 3 · 8/10 books lean ARI · 8.8% tie
//
// - value   `lineValueProbability`, the stale-line worth. It leads because it
//           is the thesis, and it is the one a pick'em tool would quote.
// - crosses only when a key number is in the win set our number gains over
//           a book's POSTED number (`computeEdge().keyNumbers`).
// - lean    `bookTally`, on de-vigged numbers, as a fraction: the denominator
//           is the confidence. Not the posted-movement count, which is a
//           different fact and lives in the Books panel.
// - tie     `pushProbability`, whenever it would not round away.
//
// The point gap, the movement count and σ are not lost: the captions above the
// bar carry the first, and the Books panel carries the other two.
import {
  bookTally,
  EDGE_BAND_ORDER,
  edgeBand,
  formatLineValue,
  tieLabel,
} from "./edge.js";

export function bandLabel(lineValue) {
  const key = edgeBand(lineValue);
  return EDGE_BAND_ORDER.find((band) => band.key === key)?.label ?? "";
}

export function sideAbbr(game, side) {
  if (!side) return "";
  return side === "home" ? game.homeTeam?.abbr ?? "" : game.awayTeam?.abbr ?? "";
}

// "53.4%". Every measurable game has one, including below 50% (a game sitting
// on 3 is ~45% to win outright) and at zero line value — there is no "No edge"
// state any more.
export function winLabel(edge) {
  if (!edge?.measurable || edge.winProbability == null) return null;
  return `${edge.winProbability.toFixed(1)}%`;
}

export function verdictLine(game, edge, tally = bookTally(game, edge)) {
  if (!edge?.measurable) return [];
  const items = [{ key: "value", text: `${formatLineValue(edge.lineValueProbability)} value` }];

  const key = edge.keyNumbers?.[0];
  if (key != null) items.push({ key: "crosses", text: `crosses ${key}` });

  if (tally?.compared) {
    items.push({
      key: "lean",
      text: `${tally.for}/${tally.compared} books lean ${sideAbbr(game, edge.side)}`,
    });
  }

  const tie = tieLabel(edge.pushProbability);
  if (tie) items.push({ key: "tie", text: tie });
  return items;
}

// The Books panel's agreement clause: "±0.3", the spread (σ) of the books'
// numbers. Moved off the card's line, where "σ 0.27" was jargon on a phone.
// Empty with fewer than two books, which is no spread of opinion at all.
export function agreementLabel(game) {
  if (game?.stdMeasurable === false || game?.std == null) return "";
  const std = Number(game.std);
  if (!Number.isFinite(std)) return "";
  return `±${std.toFixed(1)}`;
}

// "(7 of 8 moved off it)", the posted-movement count (`postedMovementTally`),
// moved off the card into the Books panel beside the sentence about which
// books still post our number. Only printed when a book has moved: "(0 of 8
// moved off it)" under "No book still posts KC -3" would read as a
// contradiction, when the books at -3.5 have left our number without leaving
// our win set (a half point off an integer only turns a tie into a loss).
export function movedNote(movement) {
  if (!movement?.compared || !movement.beyond) return "";
  return `(${movement.beyond} of ${movement.compared} moved off it)`;
}

// The text colour for a band, shared by the card and the picked row so a Flat
// pick never reads green: slate, yellow, then lime for both Solid and Strong.
// Strong is rare (about one game a week), so it is told apart by its word,
// its meter and BAND_GLOW (Strong only) rather than by a second green.
export const BAND_TEXT_CLASS = {
  strong: "text-strength-lime",
  solid: "text-strength-lime",
  slight: "text-strength-slight",
  flat: "text-slate-200",
};

// A glow under the card's win %, on Strong only: it is what separates Strong
// from Solid, which share the lime. On every band it read as tacky.
export const BAND_GLOW = {
  strong: "0 0 14px rgba(54, 255, 0, 0.5)",
};

// The picked row: "53.4% · Solid", plus "8.8% tie" when a tie is possible.
// Only when the pick is on the side the model recommends; otherwise the row
// says "Edge on X" and has no number of its own to print.
export function pickedSummary(edge, pickedSide) {
  if (!edge?.measurable || edge.side !== pickedSide) return null;
  const win = winLabel(edge);
  if (win == null) return null;
  return {
    win,
    bandKey: edgeBand(edge.lineValueProbability),
    band: bandLabel(edge.lineValueProbability),
    tie: tieLabel(edge.pushProbability),
  };
}
